import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Should I really rotate sectors? Isn\'t that market-timing?',
    a: 'There\'s a difference between TIMING (selling everything and buying back) and TILTING (changing the proportions of new contributions). This guide is about tilting. You keep your core long-term positions; you just point new money toward whatever sector the macro setup favors. Over decades that compounds into meaningfully better results without ever needing a "sell-everything" call.',
  },
  {
    q: 'How do I know what part of the rate cycle we\'re in?',
    a: 'Watch the Federal Reserve\'s rate decisions and the 10-year Treasury yield. When the Fed is cutting and the 10-year is falling, you\'re in a "rate-cut" environment that favors utilities and REITs. When the Fed is hiking or rates are rising, financials lead. When inflation is running hot (CPI above 4%), energy and materials win. The DiviDrip Screener lets you filter by sector once you\'ve picked your tilt.',
  },
  {
    q: 'Does this matter for long-term investors?',
    a: 'Less than you\'d think — but more than zero. Over 30 years a diversified dividend portfolio wins regardless. But over any given 3-5 year stretch, the leading sector outperforms the laggard by 5-15% per year. Tilting new contributions captures part of that without requiring you to be perfect.',
  },
  {
    q: 'Which sectors does DiviDrip make it easiest to find?',
    a: 'All of them. The Screener supports sector and industry filters; the Dashboard shows your portfolio\'s sector allocation pie; the Rebalancer recommends adjustments if any sector drifts too far from target. Picking your tilt is the easy part — DiviDrip handles the surfacing.',
  },
];

export default function LearnSectorRotation() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Sector Rotation for Dividend Investors — Which Sectors Win in Each Rate Environment | DiviDrip"
      description="Utilities and REITs love falling rates. Financials love rising ones. Energy loves inflation. A practical playbook for tilting your dividend allocation based on the macro setup — without churning your core positions."
      canonical="https://twylightcrow.com/learn/sector-rotation-for-dividend-investors"
      guidePath="/learn/sector-rotation-for-dividend-investors"
      sectionLabel="Strategy & Psychology"
      sectionAnchor="strategy"
      kicker="Learn · Macro playbook"
      h1="Sector Rotation for Dividend Investors — Which Sectors Win in Each Rate Environment"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/bond-yields-vs-dividend-yields', label: 'Bond yields vs dividend yields' },
        { to: '/learn/four-phases', label: 'The 4 phases of every dividend investor' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs' },
        { to: '/learn/covered-call-etfs', label: 'Covered-call ETFs — what JEPI / JEPQ actually do' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Kings vs Aristocrats vs Achievers' },
      ]}
    >
      <p>
        Different sectors win in different macro environments. Knowing which is
        which doesn&apos;t require crystal-ball forecasting — it just requires
        knowing the playbook. This is the tilt, not the timing.
      </p>

      <h2>The four macro environments</h2>
      <p>
        Every macro setup since the 1970s has been a combination of two variables:
        <strong> rates (rising or falling)</strong> and{' '}
        <strong>inflation (high or low)</strong>. Four quadrants, four playbooks.
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Environment</th><th>What\'s happening</th><th>Sectors that win</th><th>Sectors that lag</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Rate cuts, low inflation</strong></td>
            <td>Fed easing, economy slowing, bond yields falling</td>
            <td>Utilities, REITs, consumer staples</td>
            <td>Financials, energy</td>
          </tr>
          <tr>
            <td><strong>Rate hikes, low inflation</strong></td>
            <td>Fed normalizing, growth steady</td>
            <td>Financials (banks), industrials</td>
            <td>Utilities, REITs (rate-sensitive)</td>
          </tr>
          <tr>
            <td><strong>High inflation, slow growth (stagflation)</strong></td>
            <td>1970s-style — rare but painful</td>
            <td>Energy, materials, gold miners</td>
            <td>Long-duration utilities, consumer discretionary</td>
          </tr>
          <tr>
            <td><strong>High inflation, strong growth</strong></td>
            <td>Demand-pull inflation, 2021-2022 type setup</td>
            <td>Energy, materials, financials</td>
            <td>Utilities, REITs, low-margin consumer</td>
          </tr>
        </tbody>
      </table>

      <h2>Why each sector responds the way it does</h2>

      <h3>Utilities (XLU, NEE, SO, DUK)</h3>
      <p>
        Utilities borrow heavily to fund infrastructure. When rates fall, their
        borrowing costs drop — straight to the bottom line. Their dividends are
        long-duration cash flows; lower discount rates make those future dividends
        worth more today. Inverse: when rates rise, utility stocks fall first.
      </p>

      <h3>REITs (O, VICI, STAG, equity REIT ETFs)</h3>
      <p>
        REITs are also long-duration income vehicles AND they finance properties
        with debt. The double-sensitivity to rates makes them outperform sharply
        in cutting cycles and underperform sharply in hiking cycles. See{' '}
        <Link to="/learn/reits-vs-bdcs-vs-cefs">REITs vs BDCs vs CEFs</Link> for
        the structural detail.
      </p>

      <h3>Financials / Banks (JPM, BAC, WFC, USB)</h3>
      <p>
        Banks make money on the spread between what they pay depositors and what
        they earn on loans. Higher rates with a steep yield curve = wider spread
        = higher net interest income. When the curve inverts (short rates above
        long rates), bank profits compress — that&apos;s the warning sign for the
        sector.
      </p>

      <h3>Energy (XOM, CVX, EPD, energy MLPs)</h3>
      <p>
        Oil and gas producers benefit directly from inflation — their output
        prices rise with the broader price level. In inflationary periods they
        post record FCF and often pay variable dividends or special distributions
        on top of the regular payout. In deflationary periods they get crushed
        as commodities fall.
      </p>

      <h3>Consumer Staples (PG, KO, KMB, CL)</h3>
      <p>
        The Switzerland of dividend stocks. They underperform in roaring growth
        markets but provide ballast in recessions, deflation, and rate-cut
        environments. Their dividends keep paying through everything — most are
        Dividend Aristocrats or Kings.
      </p>

      <h2>The 2026 setup, as of early year</h2>
      <p>
        As of early 2026 the Fed has paused after a year of cuts. Rates are
        moderating but still above 4%. Inflation has cooled but isn&apos;t at
        target. That&apos;s a &quot;late-cycle, rate-cuts-coming&quot; setup, which
        historically favors utilities and REITs over financials and energy. If
        you&apos;re tilting new contributions in early 2026, those are the natural
        homes.
      </p>
      <p>
        This is not a prediction. It&apos;s a tilt based on a playbook. If the
        macro changes — if inflation re-accelerates or growth stalls — the tilt
        changes with it. The DiviDrip Screener makes it easy to find quality
        names within whichever sector you pick.
      </p>

      <h2>The practical tilt — how much should I shift?</h2>
      <ol>
        <li>
          <strong>Never abandon your core.</strong> 70-80% of your portfolio
          should be diversified across sectors regardless of the macro. KO, JNJ,
          MSFT, JPM, SCHD-type ballast.
        </li>
        <li>
          <strong>Tilt new money 60-70% toward the favored sectors.</strong>{' '}
          If you&apos;re contributing $1,000/month and utilities + REITs are
          favored, point $600-$700 of it there. Keep $300-$400 spread across the
          rest so you&apos;re not concentrated.
        </li>
        <li>
          <strong>Rebalance once a year.</strong> If the macro setup hasn&apos;t
          changed, the tilt continues. If it has, rotate the new contributions —
          but leave the existing positions in place unless one is fundamentally
          impaired.
        </li>
        <li>
          <strong>Check your sector allocation on the Dashboard pie.</strong>{' '}
          DiviDrip shows you exactly how concentrated each sector is. If
          utilities have crept from 15% to 28% of your portfolio, you&apos;ve
          tilted enough — back off the new contributions there.
        </li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        Sector rotation is a tilt, not a timing call. You don&apos;t need to predict
        the Fed. You just need to read the current environment (rate cycle +
        inflation) and point a portion of new contributions toward the sectors
        the playbook favors. Over a decade that captures a meaningful chunk of
        sector outperformance without ever requiring you to sell anything.
      </p>
    </LearnPage>
  );
}
